import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";
import { 
  BarChart2, 
  Code2, 
  Mail, 
  Phone, 
  Send,
  Menu,
  X,
  ArrowRight,
  CheckCircle2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-effect" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a 
            href="/" 
            className="text-xl md:text-2xl font-bold text-white tracking-tight"
            data-testid="logo"
          >
            Virtual<span className="text-blue-500">Agility</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <button 
              onClick={() => scrollToSection('services')}
              className="text-slate-400 hover:text-white transition-colors duration-300 text-sm font-medium"
              data-testid="nav-services"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-slate-400 hover:text-white transition-colors duration-300 text-sm font-medium"
              data-testid="nav-contact"
            >
              Contact
            </button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="rounded-full bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 font-medium transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)]"
              data-testid="header-cta"
            >
              Contact Us
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-slate-800" data-testid="mobile-nav">
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('services')}
                className="text-slate-400 hover:text-white transition-colors duration-300 text-left py-2"
                data-testid="mobile-nav-services"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-slate-400 hover:text-white transition-colors duration-300 text-left py-2"
                data-testid="mobile-nav-contact"
              >
                Contact
              </button>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="rounded-full bg-blue-600 hover:bg-blue-500 text-white w-full"
                data-testid="mobile-header-cta"
              >
                Contact Us
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section Component
const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center hero-gradient pt-20" data-testid="hero-section">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8 animate-fade-in-up">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Business Solutions
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6 animate-fade-in-up animation-delay-100">
          <span className="text-gradient">Empowering Business</span>
          <br />
          <span className="text-white">Through Virtual Agility</span>
        </h1>

        {/* Subheadline */}
        <p className="text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200">
          Expert consulting for business optimization and cutting-edge application development. 
          Transform your operations with strategic solutions built for the future.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
          <Button 
            onClick={() => scrollToSection('services')}
            className="btn-primary rounded-full bg-blue-600 hover:bg-blue-500 text-white px-8 py-6 font-medium transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] text-base"
            data-testid="hero-cta-primary"
          >
            Get Optimized
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button 
            onClick={() => scrollToSection('services')}
            variant="outline"
            className="rounded-full border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white px-8 py-6 transition-all duration-300 backdrop-blur-sm text-base bg-transparent"
            data-testid="hero-cta-secondary"
          >
            Build Your App
          </Button>
        </div>
      </div>
    </section>
  );
};

// Services Section Component
const ServicesSection = () => {
  const services = [
    {
      icon: BarChart2,
      title: "Business Optimization",
      description: "Streamline operations and maximize efficiency with our expert consulting strategies. We analyze your workflows, identify bottlenecks, and implement data-driven solutions that drive measurable results.",
      features: ["Process Analysis", "Workflow Optimization", "Performance Metrics", "Strategic Planning"],
      image: "https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnN1bHRpbmclMjBtZWV0aW5nJTIwbW9kZXJuJTIwZGFyayUyMG9mZmljZXxlbnwwfHx8fDE3NzIwNzcxOTF8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: Code2,
      title: "Application Development",
      description: "From concept to deployment, we build robust, scalable applications tailored to your needs. Our full-stack expertise ensures your digital products are built with modern technologies and best practices.",
      features: ["Custom Web Apps", "API Development", "Cloud Deployment", "Ongoing Support"],
      image: "https://images.unsplash.com/photo-1753998941540-081eed4f6397?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxzb2Z0d2FyZSUyMGRldmVsb3BlciUyMGNvZGluZyUyMGRhcmslMjBtb2RlJTIwYWVzdGhldGljc3xlbnwwfHx8fDE3NzIwNzcxOTF8MA&ixlib=rb-4.1.0&q=85"
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-blue-500 mb-4 block">
            What We Offer
          </span>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
            Our Services
          </h2>
          <p className="text-base md:text-lg text-slate-400 max-w-xl mx-auto">
            Comprehensive solutions designed to elevate your business operations and digital presence.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="service-card group relative p-8 rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden"
              data-testid={`service-card-${index}`}
            >
              {/* Background Image */}
              <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                <img 
                  src={service.image} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center mb-6 group-hover:bg-blue-600/20 transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-blue-500" />
                </div>

                {/* Title */}
                <h3 className="text-xl md:text-2xl font-medium tracking-normal text-white mb-4">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-slate-400 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-blue-500 mb-4 block">
            Get In Touch
          </span>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
            Contact Us
          </h2>
          <p className="text-base md:text-lg text-slate-400 max-w-xl mx-auto">
            Ready to transform your business? Let's start a conversation.
          </p>
        </div>

        {/* Contact Grid */}
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="input-glow bg-slate-900/50 border-slate-800 text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg h-12 px-4 transition-all duration-300 w-full"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="input-glow bg-slate-900/50 border-slate-800 text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg h-12 px-4 transition-all duration-300 w-full"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  rows={5}
                  className="input-glow bg-slate-900/50 border-slate-800 text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-4 py-3 transition-all duration-300 w-full resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary rounded-full bg-blue-600 hover:bg-blue-500 text-white px-8 py-6 font-medium transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] w-full sm:w-auto"
                data-testid="contact-submit"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Send Message
                    <Send className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2">
            <div className="glass-effect rounded-2xl p-8 h-full">
              <h3 className="text-xl font-medium text-white mb-6">
                Contact Information
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Email</p>
                    <a 
                      href="mailto:info@virtualagility.shop" 
                      className="text-white hover:text-blue-400 transition-colors"
                      data-testid="contact-email-link"
                    >
                      info@virtualagility.shop
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Phone</p>
                    <a 
                      href="tel:+18443513956" 
                      className="text-white hover:text-blue-400 transition-colors"
                      data-testid="contact-phone-link"
                    >
                      +1 (844) 351-3956
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-800">
                <p className="text-slate-400 text-sm leading-relaxed">
                  Based in the digital realm, we serve clients globally. 
                  Our team is ready to discuss your next project and help 
                  bring your vision to life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-8 border-t border-slate-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <a 
            href="/" 
            className="text-lg font-bold text-white tracking-tight"
          >
            Virtual<span className="text-blue-500">Agility</span>
          </a>
          
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} Virtual Agility. All rights reserved.
          </p>

          <a 
            href="http://www.virtualagility.shop"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
            data-testid="footer-shop-link"
          >
            virtualagility.shop
          </a>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#020617]">
      <Toaster 
        position="top-right" 
        richColors 
        theme="dark"
      />
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
